import React, { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, User, Trophy, XCircle, CalendarPlus, ExternalLink, Pencil } from "lucide-react"
import { AppShell } from "src/components/layout/AppShell"
import { Button } from "src/components/ui/button"
import { Badge } from "src/components/ui/badge"
import { Skeleton } from "src/components/ui/skeleton"
import { useToast } from "src/components/ui/toast"

import { useDeal, useWinDeal, useLoseDeal } from "src/features/deals/hooks/useDeals"
import { formatMoney, formatDate, DEAL_STATUS_BADGE, DEAL_STATUS_LABEL } from "src/features/deals/constants"
import { CreateBookingDialog } from "src/features/bookings/components/CreateBookingDialog"
import { EditDealDialog } from "src/features/deals/components/EditDealDialog"
import { NotesPanel } from "src/features/notes/NotesPanel"
import { TasksPanel } from "src/features/tasks/components/TasksPanel"
import { FollowUpsPanel } from "src/features/followups/components/FollowUpsPanel"
import { TagEntitySection } from "src/features/tags/components/TagEntitySection"
import { DocumentList } from "src/features/documents/components/DocumentList"

const DEAL_DOCUMENT_CATEGORIES = ["kyc", "sales_allotment", "agreement", "receipt"]

export default function DealDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { deal, loading, reload } = useDeal(id)
  const winDeal = useWinDeal()
  const loseDeal = useLoseDeal()
  const [bookingOpen, setBookingOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)

  async function handleWin() {
    try {
      await winDeal.mutate(id)
      toast({ title: "Deal won 🎉", description: "You can now create a booking." })
      reload()
    } catch (e) {
      toast({ variant: "destructive", title: "Could not win deal", description: Array.isArray(e) ? e[0] : "" })
    }
  }

  async function handleLose() {
    const reason = window.prompt("Reason for losing this deal? (optional)") ?? undefined
    try {
      await loseDeal.mutate(id, reason)
      toast({ title: "Deal marked lost" })
      reload()
    } catch (e) {
      toast({ variant: "destructive", title: "Could not update deal", description: Array.isArray(e) ? e[0] : "" })
    }
  }

  if (loading && !deal) {
    return <AppShell><div className="px-8 py-7"><Skeleton className="h-40 w-full max-w-2xl rounded-2xl" /></div></AppShell>
  }
  if (!deal) {
    return <AppShell><div className="px-8 py-7 text-sm text-slate-400">Deal not found.</div></AppShell>
  }

  const isWon = deal.status === "won"
  const isOpen = deal.status === "open"

  return (
    <AppShell>
      <div className="px-8 py-7 space-y-5">
        <button onClick={() => navigate("/deals")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Back to deals
        </button>

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">{deal.name || `Deal #${deal.id}`}</h1>
              <Badge variant={DEAL_STATUS_BADGE[deal.status] || "secondary"}>
                {DEAL_STATUS_LABEL[deal.status] || deal.status}
              </Badge>
            </div>
            <p className="mt-1 text-[13px] text-slate-500">
              {deal.stage?.name || "—"} · {formatMoney(deal.value)}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setEditOpen(true)}>
              <Pencil className="h-4 w-4" /> Edit
            </Button>
            {isOpen && (
              <>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={handleLose} disabled={loseDeal.isPending}>
                  <XCircle className="h-4 w-4 text-red-500" /> Mark lost
                </Button>
                <Button size="sm" className="gap-1.5" onClick={handleWin} disabled={winDeal.isPending}>
                  <Trophy className="h-4 w-4" /> Mark won
                </Button>
              </>
            )}
            {isWon && (
              <Button size="sm" className="gap-1.5" onClick={() => setBookingOpen(true)}>
                <CalendarPlus className="h-4 w-4" /> Create booking
              </Button>
            )}
          </div>
        </div>

        {/* Tags */}
        <TagEntitySection
          taggableType="Deal"
          taggableId={deal.id}
          initialTags={deal.tags || []}
          variant="inline"
          onChanged={reload}
        />

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]">
          {/* Lead card */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-2 text-xs text-slate-400">Lead</div>
            {deal.lead ? (
              <button
                onClick={() => navigate(`/leads/${deal.lead.id}`)}
                className="flex w-full items-center gap-3 rounded-lg p-1.5 text-left hover:bg-slate-50"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-700">
                  {(deal.lead.name || "?").slice(0, 1).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-slate-800">{deal.lead.name}</div>
                  <div className="truncate text-xs text-slate-400">{deal.lead.phone || deal.lead.status}</div>
                </div>
                <ExternalLink className="h-3.5 w-3.5 text-slate-300" />
              </button>
            ) : (
              <div className="flex items-center gap-2 text-sm text-slate-400"><User className="h-4 w-4" /> No lead linked</div>
            )}
          </div>

          {/* Details + notes */}
          <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="mb-3 text-xs text-slate-400">Deal details</div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <Field k="Value" v={formatMoney(deal.value)} />
              <Field k="Stage" v={deal.stage?.name} />
              <Field k="Owner" v={deal.owner?.full_name} />
              {deal.channel_partner && (
                <Field k="Channel Partner" v={`${deal.channel_partner.name} (${deal.channel_partner.referral_code || "no code"})`} />
              )}
              <Field k="Expected close" v={formatDate(deal.expected_close_on)} />
              {deal.won_at && <Field k="Won on" v={formatDate(deal.won_at)} />}
              {deal.lost_at && <Field k="Lost on" v={formatDate(deal.lost_at)} />}
              {deal.lost_reason && <Field k="Lost reason" v={deal.lost_reason} />}
              <Field k="Created" v={formatDate(deal.created_at)} />
            </dl>
          </div>

          <FollowUpsPanel parentType="deals" parentId={id} />
          <TasksPanel parentType="deals" parentId={id} />
          <DocumentList scope={{ type: "deal", id }} categories={DEAL_DOCUMENT_CATEGORIES} />
          <NotesPanel notable="deals" id={id} />
          </div>
        </div>
      </div>

      <CreateBookingDialog
        open={bookingOpen}
        deal={deal}
        onClose={() => setBookingOpen(false)}
        onSuccess={(booking) => booking?.id && navigate(`/bookings/${booking.id}`)}
      />

      <EditDealDialog
        open={editOpen}
        deal={deal}
        onClose={() => setEditOpen(false)}
        onSaved={reload}
      />
    </AppShell>
  )
}

function Field({ k, v }) {
  return (
    <div>
      <dt className="text-[11px] text-slate-400">{k}</dt>
      <dd className="text-slate-700">{v || "—"}</dd>
    </div>
  )
}
